import { signal } from '@preact/signals';
import type { LogClient, VerifiedTreeHead } from '../../core/log/client';
import { readJson, writeJson } from './storage';

export interface StoredHead {
  treeSize: number;
  rootHashHex: string;
  timestamp?: number;
  fetchedAt: number;
}

const HIST_KEY = (id: string) => `ct:heads:v1:${id}`;
const HISTORY_LIMIT = 30;

/** Latest verified tree head per log, plus a small on-device history for consistency checks. */
export const treeHeads = signal<Record<string, VerifiedTreeHead>>({});
const inflight = new Map<string, Promise<VerifiedTreeHead>>();

export function headHistory(logId: string): StoredHead[] {
  return readJson<StoredHead[]>(HIST_KEY(logId), []);
}

function remember(logId: string, th: VerifiedTreeHead): void {
  const hist = headHistory(logId).filter((h) => h.treeSize !== th.treeSize);
  const entry: StoredHead = { treeSize: th.treeSize, rootHashHex: th.rootHash.toHex(), fetchedAt: th.fetchedAt };
  if (th.timestamp !== undefined) entry.timestamp = th.timestamp;
  hist.push(entry);
  hist.sort((a, b) => a.treeSize - b.treeSize);
  writeJson(HIST_KEY(logId), hist.slice(-HISTORY_LIMIT));
}

export function refreshTreeHead(client: LogClient, force = false): Promise<VerifiedTreeHead> {
  const id = client.log.id;
  const cached = treeHeads.value[id];
  if (cached && !force && Date.now() - cached.fetchedAt < 30_000) return Promise.resolve(cached);
  let p = inflight.get(id);
  if (p) return p;
  p = client
    .getTreeHead()
    .then((th) => {
      treeHeads.value = { ...treeHeads.value, [id]: th };
      if (th.signature.status !== 'failed') remember(id, th);
      return th;
    })
    .finally(() => inflight.delete(id));
  inflight.set(id, p);
  return p;
}
